import { Button } from '@civilgrid/ui/components';
import { Link } from '@tanstack/react-router';
import { bbox, center } from '@turf/turf';
import type { FeatureCollection, MultiPolygon } from 'geojson';
import { type ReactElement, useEffect, useMemo, useState } from 'react';
import * as MapGL from 'react-map-gl/mapbox';
import { PROJECT_AREA_FILL_COLOR, PROJECT_AREA_LINE_COLOR } from '~/colors';
import { drfStatusLabels } from '~/common/drfUtils';
import { type DrfRequestStatus, MapStyle } from '~/common/types';
import { MapComponent } from '~/components2/Map';
import type { InitialViewState } from '~/components2/Map/common';
import CriticalInfraPolygons from './criticalInfrastructure/CriticalInfraPolygons';

interface PopupProps {
  lngLat: { lng: number; lat: number };
  content: ReactElement;
}

interface PolygonsProps {
  requests: FeatureCollection<MultiPolygon>;
  closeDialog: () => void;
  setPopup: (props: PopupProps | null) => void;
}

const requestPropsToPopupContent = (
  id: string,
  requestId: string,
  status: DrfRequestStatus,
  name: string,
  area: string,
  requestorName: string,
  requestorOrganization: string,
  closeDialog: () => void,
) => {
  return (
    <div className="min-w-[200px] space-y-1 p-2">
      <div className="mb-2 font-mono text-2xs uppercase text-secondary">
        {requestId} [{drfStatusLabels[status as DrfRequestStatus]}]
      </div>
      <div className="text-2xs font-semibold uppercase">Project</div>
      <div>{name}</div>
      <div>{area} acres</div>
      <br />
      <div className="text-2xs font-semibold uppercase">Requestor</div>
      <div>{requestorName}</div>
      <div>{requestorOrganization}</div>
      <br />
      <Link to="/dataRequestFulfillment" search={{ requestId: id }}>
        <Button onClick={closeDialog} variant="secondary">
          Open Request
        </Button>
      </Link>
    </div>
  );
};

export function Polygons({ setPopup, closeDialog, requests }: PolygonsProps) {
  const { current: map } = MapGL.useMap();
  useEffect(() => {
    function projectPolygonClick({ features, lngLat }: mapboxgl.MapMouseEvent & mapboxgl.Event & { features?: any[] }) {
      if (features?.[0]) {
        const { id, requestId, name, area, status, requestor } = features[0].properties!;
        const _requestor = JSON.parse(requestor);
        setPopup({
          lngLat,
          content: requestPropsToPopupContent(
            id,
            requestId,
            status,
            name,
            area,
            _requestor.name,
            _requestor.organization,
            closeDialog,
          ),
        });
      }
    }

    map?.on('click', 'project-polygon', projectPolygonClick);

    return () => {
      map?.off('click', 'project-polygon', projectPolygonClick);
    };
  });

  return (
    <MapGL.Source id="requestPolygons" type="geojson" data={requests}>
      <MapGL.Layer
        type="line"
        paint={{
          'line-color': PROJECT_AREA_LINE_COLOR,
          'line-width': 4,
          'line-dasharray': [3, 1],
        }}
      />
      <MapGL.Layer
        id="project-polygon"
        type="fill"
        paint={{ 'fill-color': PROJECT_AREA_FILL_COLOR, 'fill-opacity': 0.15 }}
      />
    </MapGL.Source>
  );
}

interface Props {
  requests: FeatureCollection<MultiPolygon>;
  closeDialog: () => void;
  isCriticalInfraVisible: boolean;
}

export default function RequestsMap({ requests, closeDialog, isCriticalInfraVisible }: Props) {
  const { drfMap } = MapGL.useMap();
  const [popup, setPopup] = useState<null | PopupProps>(null);
  const initialState: InitialViewState = useMemo(() => {
    if (requests.features.length > 0) {
      const _bbox = bbox(requests);
      return {
        bounds: [
          [_bbox[0], _bbox[1]],
          [_bbox[2], _bbox[3]],
        ] as any,
      };
    } else {
      return {};
    }
  }, [requests]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const requestsList = useMemo(() => {
    return requests.features.map((r) => {
      const c = center(r.geometry);
      const { id, requestId, name, area, status, requestor } = r.properties!;
      return (
        <div
          key={id}
          className="cursor-pointer border-b py-1"
          onClick={() => {
            setTimeout(() => {
              drfMap?.flyTo({
                center: [c.geometry.coordinates[0], c.geometry.coordinates[1]],
                zoom: 15,
                duration: 250,
              });
            });
            setTimeout(() => {
              setPopup({
                lngLat: {
                  lng: c.geometry.coordinates[0],
                  lat: c.geometry.coordinates[1],
                },
                content: requestPropsToPopupContent(
                  id,
                  requestId,
                  status,
                  name,
                  area,
                  requestor.name,
                  requestor.organization,
                  closeDialog,
                ),
              });
            }, 300);
          }}
        >
          <div className="font-semibold text-secondary">{name}</div>
          <div className="text-xs text-secondary">{requestor.organization}</div>
        </div>
      );
    });
  }, [requests, setPopup, closeDialog, drfMap]);

  return (
    <div className="flex h-[60vh] w-[80vw]">
      <div className="w-1/5 overflow-y-scroll">{requestsList}</div>
      <MapComponent id="drfMap" measureTool={false} initialViewState={initialState} mapStyle={MapStyle.civilGridLight}>
        {popup && (
          <MapGL.Popup
            key={`${popup.lngLat.lng},${popup.lngLat.lat}`}
            longitude={popup.lngLat.lng}
            latitude={popup.lngLat.lat}
            onClose={() => setPopup(null)}
            closeOnClick={true}
            closeOnMove={true}
            closeButton={false}
          >
            {popup.content}
          </MapGL.Popup>
        )}

        {isCriticalInfraVisible && <CriticalInfraPolygons setPopup={setPopup} />}
        <Polygons closeDialog={closeDialog} setPopup={setPopup} requests={requests} />
      </MapComponent>
    </div>
  );
}
